import React, { memo, useMemo, useState } from 'react';

import { Button, DynamicIconWrapper, IconButton, Modal } from '@components/common';
import Confirm from '@components/common/Confirm';

import { Icons } from '@icons/icons';

import useMutateApplyConfigs from '@hooks/configurationDataModelManagement/useMutateApplyConfigs';
import useMutateClearConfigs from '@hooks/configurationDataModelManagement/useMutateClearConfigs';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { formatDate } from '@utils/date';

import { ESnackbarTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';

import styles from './ValidationRules.module.scss';

const { sweep, refresh, set, info } = Icons;

const dateFormat = 'dd/MM/yyyy hh:mm a';

interface IValidationRulesProps {
	isChanged: boolean;
	isSet: boolean;
	updatedAt: number | null;
	countOfRecords: number;
}

const ValidationRules = ({
	isChanged,
	isSet,
	updatedAt,
	countOfRecords,
}: IValidationRulesProps) => {
	const dispatch = useAppDispatch();
	const [isSetConfig, setIsSetConfig] = useState(false);
	const [isUpdateConfig, setIsUpdateConfig] = useState(false);
	const [isClearConfig, setIsClearConfig] = useState(false);

	const handleCloseConfirmModals = () => {
		setIsSetConfig(false);
		setIsUpdateConfig(false);
		setIsClearConfig(false);
	};

	const openSetConfigModal = () => setIsSetConfig(true);
	const openUpdateConfigModal = () => setIsUpdateConfig(true);
	const openClearConfigModal = () => setIsClearConfig(true);

	const { applyConfigs, isLoadingApplyConfigs } = useMutateApplyConfigs({
		onSuccess: () => {
			setIsSetConfig(false);
			setIsUpdateConfig(false);

			dispatch(setSnackbar({
				message: isSetConfig
					? responseMessages.generateValidationRulesConfig
					: responseMessages.updateValidationRulesConfig,
				type: ESnackbarTypes.success,
			}));
		},
	});

	const { clearConfigs, isLoadingClearConfigs } = useMutateClearConfigs({
		onSuccess: () => {
			setIsClearConfig(false);

			dispatch(setSnackbar({
				message: responseMessages.deleteValidationRulesConfig,
				type: ESnackbarTypes.success,
			}));
		},
	});

	const isShowSetRulesButton = useMemo(() =>
		(countOfRecords ? !isSet : (isSet || isChanged)), [countOfRecords, isSet, isChanged]);

	return (
		<div className={styles.validationRulesContainer}>
			<Modal
				isLoading={isLoadingApplyConfigs}
				isOpen={isSetConfig}
				onClose={handleCloseConfirmModals}
			>
				<Confirm
					onSubmit={applyConfigs}
					question='Are you sure you want to set the validation rules?'
				>
					This Action will affect the validation rules for set Golden Settings in Consistency Check.
				</Confirm>
			</Modal>
			<Modal
				isLoading={isLoadingApplyConfigs}
				isOpen={isUpdateConfig}
				onClose={handleCloseConfirmModals}
			>
				<Confirm
					onSubmit={applyConfigs}
					question='Are you sure you want to update the validation rules?'
				>
					This Action may affect the validation rules for set Golden Settings in Consistency Check
				</Confirm>
			</Modal>
			<Modal
				isLoading={isLoadingClearConfigs}
				isOpen={isClearConfig}
				onClose={handleCloseConfirmModals}
			>
				<Confirm
					onSubmit={clearConfigs}
					question='Are you sure you want to delete the validation rules?'
				>
					This Action will delete all validation rules for set Golden Settings in Consistency Check.
				</Confirm>
			</Modal>
			<div className={styles.info}>
				<div className={styles.title}>
					Validation rules:
					<DynamicIconWrapper
						icon={info}
						tooltipLabel='Validation rules for Golden Settings in Consistency Check'
						tooltipPlacement='top'
					/>
				</div>
				{updatedAt
					? <>Last updated: {formatDate(updatedAt, dateFormat)}</>
					: 'Not set'
				}
			</div>
			<div className={styles.buttons}>
				{isShowSetRulesButton && (
					<Button
						icon={set()}
						isShadow
						onClick={openSetConfigModal}
						text='Set rules'
					/>
				)}
				{(isSet && isChanged) && (
					<Button
						icon={refresh()}
						isShadow
						onClick={openUpdateConfigModal}
						text='Update rules'
					/>
				)}
				{isSet && (
					<IconButton
						icon={sweep}
						onClick={openClearConfigModal}
						tooltipLabel='Clear validation rules'
					/>
				)}
			</div>
		</div>
	);
};

export default memo(ValidationRules);
